import { I18n, type SupportedLocale } from "@repo/core";
import type { Category, CategoryGroup } from "../lib/schema";
import { DEFAULT_CATEGORIES } from "../lib/defaultCategories";
import { ENGLISH_STRINGS, translations } from "./translations";

const DEFAULT_IDS = new Set(DEFAULT_CATEGORIES.map((c) => c.id));

/** Canonical English names written to IndexedDB for default categories. */
export const CANONICAL_CATEGORY_NAMES: Record<string, string> = Object.fromEntries(
  DEFAULT_CATEGORIES.map((c) => [c.id, c.name])
);

/** Collect every translated label for a default category id (all locales). */
function knownLabelsForCategory(id: string): Set<string> {
  const key = `cat.${id}`;
  const labels = new Set<string>([CANONICAL_CATEGORY_NAMES[id]].filter(Boolean));
  for (const dict of Object.values(translations)) {
    if (dict?.[key]) labels.add(dict[key]);
  }
  if (ENGLISH_STRINGS[key]) labels.add(ENGLISH_STRINGS[key]);
  return labels;
}

export function isDefaultCategoryId(id: string): boolean {
  return DEFAULT_IDS.has(id);
}

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/** Non-React translator (seed, clear, restore). */
export function createTranslator(locale: SupportedLocale): TranslateFn {
  const i18n = new I18n(locale, translations);
  return (key, vars) => i18n.t(key, vars);
}

/**
 * Display label for a category.
 * Default taxonomy ids → i18n unless the user customized the stored name.
 * Custom categories → stored name.
 */
export function categoryLabel(
  cat: Pick<Category, "id" | "name"> | undefined | null,
  t: TranslateFn,
  fallback = "—"
): string {
  if (!cat) return fallback;
  if (!isDefaultCategoryId(cat.id)) return cat.name || fallback;

  const known = knownLabelsForCategory(cat.id);
  if (known.has(cat.name) || !cat.name) {
    return t(`cat.${cat.id}`);
  }
  // User renamed this default category — respect their label
  return cat.name;
}

export function categoryLabelById(
  id: string,
  categories: Pick<Category, "id" | "name">[],
  t: TranslateFn,
  fallback?: string
): string {
  const cat = categories.find((c) => c.id === id);
  return categoryLabel(cat ?? { id, name: "" }, t, fallback ?? t("common.uncategorized"));
}

export function groupLabel(group: CategoryGroup | string, t: TranslateFn): string {
  const key = `group.${group}`;
  const translated = t(key);
  return translated === key ? group : translated;
}

export function accountTypeLabel(type: string, t: TranslateFn): string {
  const key = `accountType.${type}`;
  const translated = t(key);
  return translated === key ? type : translated;
}

/** Canonical English person tags used in demo data + filters. */
export const PERSON_CANONICAL = {
  household: "Household",
  personal: "Personal",
  partner: "Partner",
  sideHustle: "Side Hustle",
} as const;

const PERSON_LABEL_KEYS: Record<string, string> = {
  household: "person.household",
  personal: "person.personal",
  partner: "person.partner",
  sidehustle: "person.sideHustle",
  "side hustle": "person.sideHustle",
};

/** All known spellings (EN + translated) → canonical English storage value. */
function buildPersonAliasMap(t: TranslateFn): Map<string, string> {
  const map = new Map<string, string>();
  const add = (alias: string, canonical: string) => {
    map.set(alias.trim().toLowerCase(), canonical);
  };
  add(PERSON_CANONICAL.household, PERSON_CANONICAL.household);
  add(PERSON_CANONICAL.personal, PERSON_CANONICAL.personal);
  add(PERSON_CANONICAL.partner, PERSON_CANONICAL.partner);
  add(PERSON_CANONICAL.sideHustle, PERSON_CANONICAL.sideHustle);
  add(t("person.household"), PERSON_CANONICAL.household);
  add(t("person.personal"), PERSON_CANONICAL.personal);
  add(t("person.partner"), PERSON_CANONICAL.partner);
  add(t("person.sideHustle"), PERSON_CANONICAL.sideHustle);
  // Common Spanish leftovers if a prior build stored translated presets
  add("hogar", PERSON_CANONICAL.household);
  add("pareja", PERSON_CANONICAL.partner);
  add("ingresos extra", PERSON_CANONICAL.sideHustle);
  return map;
}

/** Normalize for storage / filter matching (presets → English; custom unchanged). */
export function personCanonical(storedOrDisplay: string, t: TranslateFn): string {
  const raw = storedOrDisplay.trim();
  if (!raw || raw === "All") return raw;
  const map = buildPersonAliasMap(t);
  return map.get(raw.toLowerCase()) ?? raw;
}

/** Display label for person filter chips / tables (presets translated). */
export function personLabel(stored: string, t: TranslateFn): string {
  if (!stored || stored === "All") return t("common.all");
  const canonical = personCanonical(stored, t);
  const key =
    PERSON_LABEL_KEYS[canonical.toLowerCase()] ??
    PERSON_LABEL_KEYS[canonical.toLowerCase().replace(/\s+/g, "")];
  if (key) {
    const out = t(key);
    if (out !== key) return out;
  }
  return stored;
}

export function isKnownPersonPreset(stored: string, t: TranslateFn): boolean {
  const raw = stored.trim();
  if (!raw) return false;
  return buildPersonAliasMap(t).has(raw.toLowerCase());
}

/** Demo entity label (accounts / debts / sinking funds with fixed demo ids). */
export function demoEntityLabel(
  id: string,
  storedName: string,
  t: TranslateFn
): string {
  const key = `demo.${id}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return storedName;
}

/** Default categories with localized names for the given locale (for DB writes). */
export function localizedDefaultCategories(locale: SupportedLocale): Category[] {
  const t = createTranslator(locale);
  return DEFAULT_CATEGORIES.map((c) => ({
    ...c,
    name: t(`cat.${c.id}`),
  }));
}
